import { useState } from 'react';
import { X, Check, ArrowRight, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import type { GrammarIssue } from '@/lib/grammar';
import { cn } from '@/lib/utils';
import { getDefinition, type Definition } from '@/lib/dictionary';
import { AuthorDictionary } from './AuthorDictionary';
import { Sparkles, Book } from 'lucide-react';

interface GrammarPanelProps {
    issues: GrammarIssue[];
    onFix: (issue: GrammarIssue, chosenSuggestion?: string) => void;
    onClose: () => void;
    themeColors: any;
}

export function GrammarPanel({ issues, onFix, onClose, themeColors }: GrammarPanelProps) {
    const [expandedIssue, setExpandedIssue] = useState<string | null>(null);
    const [definitions, setDefinitions] = useState<Record<string, Definition | null>>({});
    const [loading, setLoading] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'suggestions' | 'dictionary'>('suggestions');

    const toggleDefinition = async (issue: GrammarIssue) => {
        const id = `${issue.start}-${issue.text}`;
        if (expandedIssue === id) {
            setExpandedIssue(null);
            return;
        }

        setExpandedIssue(id);

        if (issue.suggestion && !definitions[issue.suggestion]) {
            setLoading(issue.suggestion);
            const def = await getDefinition(issue.suggestion);
            setDefinitions(prev => ({ ...prev, [issue.suggestion]: def }));
            setLoading(null);
        }
    };
    return (
        <div
            className="fixed right-6 top-24 bottom-24 w-80 rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.6)] border-2 flex flex-col overflow-hidden z-[60] animate-in slide-in-from-right duration-300 backdrop-blur-xl"
            style={{
                backgroundColor: themeColors.bgSecondary + 'f9',
                borderColor: themeColors.accent,
                color: themeColors.text,
                boxShadow: '0 0 0 1px rgba(255,255,255,0.05) inset'
            }}
        >
            <div className="p-4 border-b-2 flex items-center justify-between" style={{ borderColor: themeColors.border, backgroundColor: themeColors.accent + '10' }}>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full shadow-[0_0_12px_rgba(245,158,11,0.8)] animate-pulse" style={{ backgroundColor: themeColors.accent }} />
                    <h2 className="font-black text-[11px] uppercase tracking-widest" style={{ color: themeColors.accent }}>Writing Assistant</h2>
                </div>
                <button
                    onClick={onClose}
                    className="p-1 rounded-full transition-colors"
                    style={{ color: themeColors.accent }}
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="flex border-b-2" style={{ borderColor: themeColors.border }}>
                <button
                    onClick={() => setActiveTab('suggestions')}
                    className={cn(
                        "flex-1 py-3 text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all"
                    )}
                    style={{
                        backgroundColor: activeTab === 'suggestions' ? themeColors.accent + '20' : 'transparent',
                        color: activeTab === 'suggestions' ? themeColors.text : themeColors.text + '80'
                    }}
                >
                    <Sparkles className="w-3 h-3" />
                    Suggestions {issues.length > 0 && `(${issues.length})`}
                </button>
                <button
                    onClick={() => setActiveTab('dictionary')}
                    className={cn(
                        "flex-1 py-3 text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all"
                    )}
                    style={{
                        backgroundColor: activeTab === 'dictionary' ? themeColors.accent + '20' : 'transparent',
                        color: activeTab === 'dictionary' ? themeColors.text : themeColors.text + '80'
                    }}
                >
                    <Book className="w-3 h-3" />
                    Dictionary
                </button>
            </div>

            <div className="flex-1 overflow-y-auto">
                {activeTab === 'suggestions' ? (
                    <div className="p-4 space-y-4">
                        {issues.length === 0 ? (
                            <div className="min-h-[200px] flex flex-col items-center justify-center text-center space-y-2 opacity-50">
                                <Check className="w-8 h-8 text-green-500" />
                                <p className="text-sm">Your prose is looking sharp!</p>
                            </div>
                        ) : (
                            issues.map((issue) => (
                                <div
                                    key={`${issue.start}-${issue.text}`}
                                    className="p-4 rounded-xl border-2 space-y-3 transition-all duration-300 hover:shadow-lg bg-white/5 border-amber-500/20 hover:border-amber-500/50"
                                >
                                    <div className="flex items-start justify-between">
                                        <span className={cn(
                                            "px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-tighter",
                                            issue.type === 'spelling' ? "bg-red-500 text-white" :
                                                issue.type === 'grammar' ? "bg-blue-500 text-white" :
                                                    "bg-amber-500 text-white"
                                        )}>
                                            {issue.type}
                                        </span>
                                    </div>

                                    <p className="text-xs font-semibold leading-relaxed opacity-90">
                                        {issue.message}
                                    </p>

                                    <div className="flex items-center gap-2 group cursor-pointer" onClick={() => onFix(issue, issue.suggestion)}>
                                        <span className="line-through opacity-50">{issue.text}</span>
                                        <ArrowRight className="w-3 h-3 opacity-30 group-hover:opacity-100 transition-opacity" />
                                        <span className="font-bold" style={{ color: themeColors.accent }}>{issue.suggestion}</span>
                                    </div>

                                    {issue.type === 'spelling' && issue.suggestions && issue.suggestions.length > 1 && (
                                        <div className="space-y-2 pt-1">
                                            <p className="text-[10px] font-bold opacity-40 uppercase tracking-widest">Other Options</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {issue.suggestions.slice(1).map((sug, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onFix(issue, sug);
                                                        }}
                                                        className="px-2 py-1 rounded text-[10px] font-bold hover:opacity-80 transition-all border"
                                                        style={{ backgroundColor: themeColors.accent + '15', color: themeColors.text, borderColor: themeColors.border }}
                                                    >
                                                        {sug}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {issue.type === 'spelling' && issue.suggestion && (
                                        <div className="space-y-1">
                                            <button
                                                onClick={() => toggleDefinition(issue)}
                                                className="text-[10px] flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity"
                                            >
                                                <BookOpen className="w-3 h-3" />
                                                {expandedIssue === `${issue.start}-${issue.text}` ? 'Hide Definition' : 'View Definition'}
                                                {expandedIssue === `${issue.start}-${issue.text}` ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                            </button>

                                            {expandedIssue === `${issue.start}-${issue.text}` && (
                                                <div className="p-2 rounded-lg bg-black/5 text-[10px] animate-in fade-in slide-in-from-top-1 duration-200">
                                                    {loading === issue.suggestion ? (
                                                        <span className="opacity-50 italic">Fetching definition...</span>
                                                    ) : definitions[issue.suggestion] ? (
                                                        <div className="space-y-1">
                                                            <p className="font-semibold">{definitions[issue.suggestion]?.meanings?.[0]?.partOfSpeech}</p>
                                                            <p className="opacity-80">
                                                                {definitions[issue.suggestion]?.meanings?.[0]?.definitions?.[0]?.definition}
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <span className="opacity-50 italic">No definition found.</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        onClick={() => onFix(issue, issue.suggestion)}
                                        className="w-full py-3 rounded-xl text-xs font-black transition-all shadow-md active:scale-95 uppercase tracking-widest border-2 border-white/10"
                                        style={{ backgroundColor: themeColors.accent, color: themeColors.bgSecondary }}
                                    >
                                        Apply "{issue.suggestion}"
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                    <AuthorDictionary />
                )}
            </div>
        </div >
    );
}
